import React, { useEffect, useState } from 'react';
import {
  StylesProvider,
  createGenerateClassName,
} from '@material-ui/core/styles';
import ThemeProvider from '../mui/ThemeProvider';
import isEmpty from 'lodash/isEmpty';
import { Provider } from 'react-redux';
import { createStore, applyMiddleware } from 'redux';
import thunk from 'redux-thunk';
import { composeWithDevTools } from 'redux-devtools-extension';
import rootReducer from '../reducers';
import theme from '../util/theme';
import { getConfig } from '../util/api';
import logger from '../util/logger';

const generateClassName = createGenerateClassName({ seed: 'dash' });

const getApiHost = (clientHost, apiHost) => {
  if (apiHost) return apiHost;

  // use clientHost if embeded as a micro frontend
  const hostname = clientHost || window.location.hostname;

  // For local development - derive domain from current hostname
  if (hostname.includes('local')) {
    const domain = hostname.split('.').slice(1).join('.');
    return `https://dashboard-service-local.${domain}:3005`;
  }

  // prod and int uses -client (dashboards-client → dashboards)
  // other envs use -frontend (dashboards-frontend → dashboards)
  const resolvedHost = hostname.replace('-client', '').replace('-frontend', '');
  return hostname.includes('https') ? resolvedHost : `https://${resolvedHost}`;
};

export default function Wrapper({
  children,
  useRedux = true,
  ...externalProps
}) {
  const { clientHost, apiHost } = externalProps;
  const _apiHost = getApiHost(clientHost, apiHost);
  window.config = {
    ...(window.config || {}),
    DASHBOARD_API_URL: _apiHost,
  };

  const [config, setConfig] = useState(null);
  const [isConfigLoading, setIsConfigLoading] = useState(true);
  const [store, setStore] = useState(null);

  useEffect(() => {
    // Inject Figtree font if not already present
    const fontId = 'gradebook-figtree-font';
    if (!document.getElementById(fontId)) {
      const link = document.createElement('link');
      link.id = fontId;
      link.rel = 'stylesheet';
      link.href =
        'https://fonts.googleapis.com/css2?family=Figtree:ital,wght@0,300..900;1,300..900&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    // Only create redux store if useRedux is true
    if (useRedux) {
      const store = createStore(
        rootReducer,
        composeWithDevTools(applyMiddleware(thunk))
      );

      if (import.meta.hot) {
        // Enable vite hot module replacement for reducers
        import.meta.hot.accept('../reducers', () => {
          const nextRootReducer = require('../reducers/index');
          store.replaceReducer(nextRootReducer);
        });
      }
      setStore(store);
    } else {
      // Set store to true to indicate we're ready to render (but without Redux)
      setStore(true);
    }
  }, [useRedux]);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (_apiHost) {
        try {
          setIsConfigLoading(true);

          const data = await getConfig(_apiHost);
          if (cancelled) return;

          if (data.error || isEmpty(data)) {
            throw new Error(data.error);
          }
          setConfig(data);
        } catch (error) {
          if (cancelled) return;
          console.log(error);
          logger(error);
        } finally {
          if (!cancelled) {
            setIsConfigLoading(false);
          }
        }
      }
    };
    fetchData();

    return () => {
      cancelled = true;
    };
  }, [_apiHost]);

  // Wait for both config and store setup to complete
  const isReady = useRedux ? store && config : config;

  if (!isReady) {
    return null;
  }

  const childrenWithProps = React.cloneElement(children, {
    ...externalProps,
    apiHost: _apiHost,
    isConfigLoading,
    config,
  });

  const commonContent = (
    <StylesProvider generateClassName={generateClassName}>
      <ThemeProvider theme={theme}>{childrenWithProps}</ThemeProvider>
    </StylesProvider>
  );

  if (useRedux && typeof store === 'object') {
    return <Provider store={store}>{commonContent}</Provider>;
  }

  return commonContent;
}
